import React, { useContext, useEffect, useRef } from 'react';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  type HeroImageV1,
  type RestLCOPopUp,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import {
  CloseOfferButton,
  TitleTheme,
} from '@bbkFunnelComponents/modals/sections';
import { RefsContext } from '@bbkFunnelUtils/refs.context';

import './modal.scss';

type Props = {
  modalInfo: RestLCOPopUp;
  detailedAction: React.ReactElement;
  close: () => void;
};

const CopyContent = styled.div`
  overflow-y: auto;
  padding-top: 10px;
  padding-bottom: 10px;
`;

export const LCOPopup: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      hero,
      info: { title: titleP, details },
    },
  },
  detailedAction,
  close,
}) => {
  const theme = useTheme() as EmotionTheme;
  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-image`, imageRef), [imageRef]);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const actionsRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-actions`, actionsRef),
    [actionsRef]
  );

  const imgUrl = (hero as HeroImageV1).url;

  const copy = <CustomerHtml value={details} />;

  return (
    <div
      css={css`
        padding: 0;
      `}
      className={cs('lco-popup', 'display-grid', 'desktop', 'inner-spacer')}
      style={{
        borderRadius: theme.borderRadius,
        overflow: 'hidden',
      }}
    >
      <div style={{ maxHeight: '200px' }}>
        <img
          ref={imageRef}
          alt=""
          src={imgUrl}
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            max-height: 200px;
          `}
        />
        <CloseOfferButton
          newVariant
          onClick={close}
          className={cs('close-btn', 'z-10')}
        />
      </div>
      <CopyContent
        className={cs('align-self--center popup-text')}
        css={{ overflowY: 'scroll', padding: '1rem', maxHeight: '15rem' }}
      >
        <span ref={titleRef}>
          <TitleTheme title={titleP} css={{ marginBottom: '1rem' }} />
        </span>
        <div ref={descRef}>{copy}</div>
      </CopyContent>
      <div css={{ padding: '1rem' }} ref={actionsRef}>
        {detailedAction}
      </div>
    </div>
  );
};
